//
// # Symbol Pin Drawings
//

// Local Imports
import { exhaust } from "CoreUtils";
import { Point, graphics, model } from "GenAlphaModel";
import { EntityKind } from "../../entity";
import { TextLabel } from "./label";
import { MousePos } from "../../mousepos";
import { Entity } from "../../tree";
import { DrawTo } from "../../draw";
import * as style from "../../style";
import type { RedrawRequest } from "../../redraw";

// Apply the instance-port styling in two.js terms
const instancePinStyleTable: style.StyleTable = {
  shape: {
    //       light light    dark  dark
    //       no    hilited  no    hilited
    fill: style.Color.red,
    stroke: style.Color.red,
    linewidth: 1,
    cap: "round",
    join: "round",
  },
  text: {
    family: style.THE_FONT,
    style: "normal",
    weight: 700, // Typical value for "bold"
    size: 16,
    fill: style.Color.red,
    stroke: style.Special.None,
  },
};

export class SymbolPin extends Entity<model.SymbolPin> {
  //
  readonly entityKind = EntityKind.SymbolPin;
  label: TextLabel | null = null;
  override readonly cache = this.makeCache<style.Style>(instancePinStyleTable);
  get style(): style.Style {
    return this.cache.style;
  }

  // Create and add the drawn, graphical representation
  override redraw = (to: DrawTo): unknown => {
    const { data, style } = this;
    const { shape } = data;

    // Draw our shape
    (() => {
      switch (shape.kind) {
        case graphics.ElementKind.Rectangle:
          return to.rect(shape, style.shape);
        case graphics.ElementKind.Polygon:
          return to.polygon(shape, style.shape);
        case graphics.ElementKind.Ellipse:
          return to.ellipse(shape, style.shape);
        default:
          return exhaust(shape);
      }
    })();

    const { labelFmt } = data;
    if (labelFmt) {
      // Combine our `portName` with our label-format to create a text label

      const text = graphics.Text.new({ text: this.data.portName, ...labelFmt });
      this.label = new TextLabel({
        data: {
          ...text, // Text String
          labelKind: model.ExprKind.PortName, // Kind
        },
        parent: this,
      });
      this.label.redraw(to);
    }
    return null;
  };

  // Update our location
  // Sets `loc` to be the center of our hit-shape. Our label is adjusted accordingly.
  updateLoc(loc: Point): RedrawRequest {
    const oldCenter = model.schematic.PinShape.bboxCenter(this.data.shape);
    const delta = Point.sub(loc, oldCenter);
    this.data.shape = model.schematic.PinShape.translate(
      this.data.shape,
      delta
    );
    if (this.data.labelFmt) {
      this.data.labelFmt.loc = Point.add(this.data.labelFmt.loc, delta);
    }
    return this.root.requestRedraw();
  }

  abort = () => {};
  get shapeRef(): SymbolPinShape {
    return new SymbolPinShape(this);
  }

  get labelRef(): SymbolPinLabel | null {
    if (!this.label) return null;
    return new SymbolPinLabel(this, this.label);
  }
}

export class SymbolPinShape {
  readonly entityKind = EntityKind.SymbolPinShape;
  constructor(public parent: SymbolPin) {}
  get loc(): Point {
    return model.schematic.PinShape.bboxCenter(this.parent.data.shape);
  }
  hitTest = (p: MousePos): boolean => {
    const bbox = model.schematic.PinShape.bbox(this.parent.data.shape);
    return model.Bbox.contains(bbox, p.sch);
  };
  update = (data: { loc: Point }): RedrawRequest => {
    return this.parent.updateLoc(data.loc);
  };
  redraw = (to: DrawTo) => this.parent.redraw(to);
}

export class SymbolPinLabel {
  readonly entityKind = EntityKind.SymbolPinLabel;
  constructor(public parent: SymbolPin, public label: TextLabel) {}
  get loc(): Point {
    return this.label.data.loc;
  }
  hitTest = (p: MousePos): boolean => this.label.hitTest(p);
  // The label position is persisted in the pin's `labelFmt.loc`; the transient
  // `label` entity is rebuilt from it on every redraw. Write through to the pin
  // data (not just the transient label, which would be discarded on the next
  // redraw) so the move renders live and round-trips on save.
  update = (data: { loc: Point }): RedrawRequest => {
    if (this.parent.data.labelFmt) {
      this.parent.data.labelFmt.loc = Point.clone(data.loc);
    }
    this.label.update(data);
    return this.parent.root.requestRedraw();
  };
  redraw = (to: DrawTo) => this.parent.redraw(to);
}
